import type { Currency } from './enums';

/**
 * Máscara de campo de moeda "centavos da direita para a esquerda": o operador
 * digita só dígitos e os centavos preenchem a partir da direita (`99800` →
 * `998,00`). Lógica pura, sem React — os wrappers Cloudscape de cada app
 * (`apps/admin/src/components/CurrencyInput.tsx`,
 * `apps/crm/src/components/CurrencyInput.tsx`) só a desenham.
 *
 * Origem: sem issue — commit 5e7d598c6, 2026-06-22 (cupom do admin); movida
 * para `@etiquetachef/types` em 2026-09-29 19:45 +0200 (#776) para o CRM usar
 * a MESMA máscara em todo preço do pós-venda.
 *
 * Por quê: sem parse de separador decimal é impossível digitar valor ambíguo —
 * `1.998,00` (pt-BR) e `1,998.00` (en-US) dão os mesmos dígitos, e letra não
 * entra. O campo de texto anterior do CRM aceitava letras e só reclamava no
 * envio (owner, 2026-09-29).
 *
 * Arquitetura: fica em `types` e não em `ui-shared` porque o admin baniu o
 * `ui-shared` (5ee344f94, UI 100% Cloudscape) — `types` é o único pacote que
 * admin e CRM já importam. Convenção de agrupamento/decimal segue a MOEDA,
 * não a língua da UI: BRL/EUR usam vírgula decimal; USD, ponto.
 *
 * Usado por: `CurrencyInput` do admin (cupom) e do CRM (Contratação, Pós-venda
 * e pós-venda do parceiro).
 *
 * Dicas: o teto de dígitos (`maxDigits`, default 11 → 999.999.999,99) é o que
 * impede colar um número enorme; não troque `replace(/\D/g, '')` por parse
 * numérico, que reintroduziria a ambiguidade de separador.
 */
const LOCALE_BY_CURRENCY: Record<Currency, string> = {
  BRL: 'pt-BR',
  USD: 'en-US',
  EUR: 'de-DE',
};

const FORMATTERS: Record<Currency, Intl.NumberFormat> = {
  BRL: new Intl.NumberFormat(LOCALE_BY_CURRENCY.BRL, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
  USD: new Intl.NumberFormat(LOCALE_BY_CURRENCY.USD, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
  EUR: new Intl.NumberFormat(LOCALE_BY_CURRENCY.EUR, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
};

/**
 * Extrai os dígitos do texto digitado e interpreta como centavos inteiros.
 * `slice(0, maxDigits)` trava o teto; texto sem dígito devolve `null` (campo
 * vazio), nunca `0`.
 */
export function digitsToCents(raw: string, maxDigits = 11): number | null {
  const digits = raw.replace(/\D/g, '').slice(0, maxDigits);
  return digits === '' ? null : Number.parseInt(digits, 10);
}

/** Centavos → string mascarada (2 casas + agrupamento da moeda, sem símbolo). */
export function formatCentsMasked(cents: number, currency: Currency): string {
  return FORMATTERS[currency].format(cents / 100);
}

/**
 * Símbolo da moeda na convenção dela (`R$`, `US$`/`$`, `€`), para o prefixo
 * do campo. Moeda desconhecida devolve o próprio código.
 */
export function currencyMaskSymbol(currency: Currency): string {
  const locale = LOCALE_BY_CURRENCY[currency];
  const part = new Intl.NumberFormat(locale, { style: 'currency', currency })
    .formatToParts(0)
    .find((p) => p.type === 'currency');
  return part?.value ?? currency;
}
